import React, {
	useRef,
	useEffect,
	useState,
	useImperativeHandle,
} from "react";

import {
	Link,
	useLocation,
} from "react-router-dom";

import {
	gsap,
} from "gsap";

import NavMenu from "../NavMenu";

import CaseFilterIdentity from "../CaseFilterIdentity";

import useFilterFromLocation from "../../hooks/useFilterFromLocation";

import caseData from "../../data/cases/cases.json";

import {
	getAvailableCaseFilterOptions,
	getFilterLabel,
	normalizeCaseFilter,
} from "../../data/cases/caseFilterHelpers";

import {
	CASE_SCOPE_FILTERS,
	sortCaseFilterOptions,
} from "../../data/cases/caseFilterPresentation";

import styles from "./HeaderFilter.module.css";

/*
 * ============================================================
 * SECONDARY SCOPE
 * ============================================================
 *
 * Existing routing/filter behaviour stays untouched:
 *
 * all
 * work
 * solution
 *
 * They are still mutually-exclusive filters internally.
 */
const scopeFilters =
	CASE_SCOPE_FILTERS;

const scopeFilterValues =
	new Set(
		scopeFilters.map(
			(filter) =>
				filter.value,
		),
	);

/*
 * ============================================================
 * SHEET-DRIVEN TOPIC FILTERS
 * ============================================================
 */
const dynamicCaseFilters =
	sortCaseFilterOptions(
		getAvailableCaseFilterOptions(
			caseData,
		).filter(
			(filter) =>
				!scopeFilterValues.has(
					filter.value,
				),
		),
	);

/*
 * Complete label lookup.
 */
const headerCaseFilters = [
	...scopeFilters,
	...dynamicCaseFilters,
];

/*
 * Preserve original Sheet labels whenever possible.
 *
 * AI stays AI.
 * Unreal Engine stays Unreal Engine.
 */
const getHeaderFilterLabel = (
	filter,
) => {
	const normalizedFilter =
		normalizeCaseFilter(
			filter,
		);

	return (
		headerCaseFilters.find(
			(option) =>
				option.value ===
				normalizedFilter,
		)?.label ??
		getFilterLabel(
			normalizedFilter,
		)
	);
};

const Header = React.forwardRef(
	(props, ref) => {
		const location =
			useLocation();

		const curFilter =
			normalizeCaseFilter(
				useFilterFromLocation(),
			);

		const loaded =
			useRef(false);

		const dataState =
			useRef(null);

		const lclState =
			useRef(null);

		const searchBarRef =
			useRef(null);

		const searchBarDropDownRef =
			useRef(null);

		const [
			dropOpen,
			setDropOpen,
		] = useState(false);

		const headerRef =
			useRef(null);

		const logoRef =
			useRef(null);

		const lamaRef =
			useRef(null);

		const backBtnRef =
			useRef(null);

		const backTxtRef =
			useRef(null);

		const backTxtSpacerRef =
			useRef(null);

		const backTxtPrevRef =
			useRef(null);

		const backIcon =
			useRef(null);

		const contactCnt =
			useRef(null);

		const backTxtSpacerContainerRef =
			useRef(null);

		const backTxtContainerRef =
			useRef(null);

		const backTxtCurrentContainerRef =
			useRef(null);

		/*
		 * ========================================================
		 * CLOSED FILTER BAR
		 * ========================================================
		 */
		const compactFilter = {
			value:
				curFilter,

			label:
				curFilter === "all"
					? "All cases"
					: getHeaderFilterLabel(
							curFilter,
					  ),
		};

		const customBackVisibleRef =
			useRef(true);

		const CUSTOM_BACK_SHIFT =
			-25;

		const CUSTOM_BACK_DURATION =
			0.2;

		useEffect(() => {
			// Header mounted
		}, []);

		const setCustomBackVisibility =
			(
				visible,
				instant = false,
			) => {
				customBackVisibleRef.current =
					visible;

				if (
					!backBtnRef.current
				) {
					return;
				}

				gsap.killTweensOf(
					backBtnRef.current,
				);

				gsap.to(
					backBtnRef.current,
					{
						duration:
							instant
								? 0
								: CUSTOM_BACK_DURATION,

						yPercent:
							visible
								? 0
								: CUSTOM_BACK_SHIFT,

						autoAlpha:
							visible
								? 1
								: 0,

						overwrite:
							true,
					},
				);
			};

		const handleDropDown = (
			open,
			instant = false,
		) => {
			const dropDuration =
				0.3;

			if (
				!searchBarDropDownRef.current
			) {
				return;
			}

			if (open) {
				gsap.to(
					searchBarDropDownRef.current,
					{
						duration:
							instant
								? 0
								: dropDuration,

						autoAlpha:
							1,
					},
				);

				setDropOpen(
					true,
				);
			} else {
				gsap.to(
					searchBarDropDownRef.current,
					{
						duration:
							instant
								? 0
								: dropDuration,

						autoAlpha:
							0,
					},
				);

				setDropOpen(
					false,
				);
			}
		};

		const switchHeaderState =
			(
				newState,
				newData = null,
			) => {
				if (
					newState ===
					"custom_back_visibility"
				) {
					setCustomBackVisibility(
						newData?.visible !==
							false,

						newData?.instant ===
							true,
					);

					return;
				}

				if (
					(newState ==
						lclState.current &&
						JSON.stringify(
							newData,
						) ==
							JSON.stringify(
								dataState.current,
							)) ||
					(JSON.stringify(
						newData?.searchBar,
					) ==
						JSON.stringify(
							dataState.current
								?.searchBar,
						) &&
						newData?.searchBar)
				) {
					return;
				}

				lclState.current =
					newState;

				dataState.current =
					newData;

				const durationTransition =
					0.5;

				const durationTransitionBack =
					0.25;

				switch (newState) {
					case "typo":
						customBackVisibleRef.current =
							true;

						gsap.to(
							logoRef.current,
							{
								duration:
									durationTransition,

								yPercent:
									0,

								autoAlpha:
									1,

								overwrite:
									true,
							},
						);

						gsap.to(
							lamaRef.current,
							{
								duration:
									(durationTransition /
										3) *
									2,

								yPercent:
									100,

								autoAlpha:
									0,

								overwrite:
									true,
							},
						);

						gsap.to(
							backBtnRef.current,
							{
								duration:
									(durationTransitionBack /
										3) *
									2,

								yPercent:
									25,

								autoAlpha:
									0,

								overwrite:
									true,
							},
						);

						gsap.to(
							[
								headerRef.current,
							],
							{
								backgroundPosition:
									"0% 0%",
							},
						);

						gsap.to(
							searchBarRef.current,
							{
								duration:
									durationTransitionBack,

								yPercent:
									0,

								autoAlpha:
									0,

								delay:
									0,

								overwrite:
									true,
							},
						);

						gsap.to(
							contactCnt.current,
							{
								duration:
									(durationTransition /
										3) *
									2,

								yPercent:
									0,

								autoAlpha:
									1,

								overwrite:
									true,
							},
						);

						break;

					case "logo": {
						let direction =
							1;

						const customBackVisibilityManaged =
							newData
								?.customBack
								?.visibilityManaged ===
							true;

						if (
							!customBackVisibilityManaged
						) {
							customBackVisibleRef.current =
								true;
						}

						if (
							!newData?.hideNav
						) {
							gsap.to(
								lamaRef.current,
								{
									duration:
										durationTransition,

									yPercent:
										0,

									autoAlpha:
										1,

									overwrite:
										true,
								},
							);

							gsap.to(
								logoRef.current,
								{
									duration:
										(durationTransition /
											3) *
										2,

									yPercent:
										-100,

									autoAlpha:
										0,

									overwrite:
										true,
								},
							);

							gsap.to(
								contactCnt.current,
								{
									duration:
										(durationTransition /
											3) *
										2,

									yPercent:
										0,

									autoAlpha:
										1,

									overwrite:
										true,
								},
							);
						} else {
							gsap.to(
								lamaRef.current,
								{
									duration:
										(durationTransition /
											3) *
										2,

									yPercent:
										-100,

									autoAlpha:
										0,

									overwrite:
										true,
								},
							);

							gsap.to(
								logoRef.current,
								{
									duration:
										(durationTransition /
											3) *
										2,

									yPercent:
										-100,

									autoAlpha:
										0,

									overwrite:
										true,
								},
							);

							gsap.to(
								contactCnt.current,
								{
									duration:
										(durationTransition /
											3) *
										2,

									yPercent:
										-100,

									autoAlpha:
										0,

									overwrite:
										true,
								},
							);

							direction =
								-1;
						}

						gsap.killTweensOf(
							backBtnRef.current,
						);

						const Switch =
							() => {
								let curIconClass =
									"back-icon";

								switch (
									newData
										?.customBack
										?.icon
								) {
									case "arrow":
										curIconClass +=
											" arrow";

										break;

									case "cross":
										curIconClass +=
											" arrow cross";

										break;

									default:
										break;
								}

								if (
									!newData
										?.customBack
										?.textPrev
								) {
									switch (
										newData
											?.customBack
											?.color
									) {
										case "active":
											curIconClass +=
												" active";

											break;

										default:
											break;
									}
								}

								if (
									backIcon.current
								) {
									backIcon.current.className =
										curIconClass;
								}

								let curTxtClass =
									"back-txt";

								switch (
									newData
										?.customBack
										?.color
								) {
									case "active":
										curTxtClass +=
											" active";

										break;

									default:
										break;
								}

								const isVideoEsc =
									newData
										?.customBack
										?.variant ===
									"video-esc";

								if (
									isVideoEsc
								) {
									curTxtClass += ` ${styles.videoEsc}`;
								}

								if (
									backTxtRef.current
								) {
									backTxtRef.current.innerText =
										newData
											?.customBack
											?.text ??
										"";

									backTxtRef.current.className =
										curTxtClass;
								}

								if (
									backTxtCurrentContainerRef.current
								) {
									backTxtCurrentContainerRef.current.className = `back-txt-cnt${
										isVideoEsc
											? ` ${styles.videoEscContainer}`
											: ""
									}`;
								}

								if (
									newData
										?.customBack
										?.textPrev
								) {
									if (
										backTxtPrevRef.current
									) {
										backTxtPrevRef.current.innerText =
											newData
												?.customBack
												?.textPrev;
									}

									if (
										backTxtSpacerRef.current
									) {
										backTxtSpacerRef.current.innerText =
											">";
									}

									gsap.set(
										[
											backTxtContainerRef.current,
											backTxtSpacerContainerRef.current,
										],
										{
											opacity:
												1,

											display:
												"block",
										},
									);
								} else {
									if (
										backTxtPrevRef.current
									) {
										backTxtPrevRef.current.innerText =
											"";
									}

									if (
										backTxtSpacerRef.current
									) {
										backTxtSpacerRef.current.innerText =
											"";
									}

									gsap.set(
										[
											backTxtContainerRef.current,
											backTxtSpacerContainerRef.current,
										],
										{
											opacity:
												0,

											display:
												"none",
										},
									);
								}
							};

						if (
							customBackVisibilityManaged
						) {
							Switch();

							gsap.set(
								backBtnRef.current,
								{
									yPercent:
										customBackVisibleRef.current
											? 0
											: CUSTOM_BACK_SHIFT,

									autoAlpha:
										customBackVisibleRef.current
											? 1
											: 0,
								},
							);
						} else if (
							!newData
								?.customBack
								?.text
						) {
							gsap.to(
								backBtnRef.current,
								{
									duration:
										durationTransitionBack,

									yPercent:
										0,

									autoAlpha:
										0,

									delay:
										0,

									overwrite:
										true,
								},
							);
						} else {
							gsap.to(
								backBtnRef.current,
								{
									duration:
										(durationTransitionBack /
											3) *
										2,

									yPercent:
										25 *
										direction,

									autoAlpha:
										0,

									overwrite:
										true,

									onComplete:
										() => {
											Switch();

											gsap.to(
												backBtnRef.current,
												{
													duration:
														(durationTransitionBack /
															3) *
														2,

													yPercent:
														0,

													autoAlpha:
														1,

													overwrite:
														true,
												},
											);
										},
								},
							);
						}

						if (
							!newData
								?.searchBar
								?.active
						) {
							handleDropDown(
								false,
								true,
							);

							gsap.to(
								searchBarRef.current,
								{
									duration:
										durationTransitionBack,

									yPercent:
										-50,

									autoAlpha:
										0,

									delay:
										0,

									overwrite:
										true,
								},
							);
						} else {
							gsap.to(
								searchBarRef.current,
								{
									duration:
										(durationTransitionBack /
											3) *
										2,

									yPercent:
										-50,

									autoAlpha:
										0,

									overwrite:
										true,

									onComplete:
										() => {
											gsap.to(
												searchBarRef.current,
												{
													duration:
														(durationTransitionBack /
															3) *
														2,

													yPercent:
														0,

													autoAlpha:
														1,

													overwrite:
														true,
												},
											);
										},
								},
							);
						}

						gsap.to(
							[
								headerRef.current,
							],
							{
								backgroundPosition:
									"0% 0%",
							},
						);

						break;
					}

					default:
						break;
				}
			};

		useImperativeHandle(
			ref,
			() => {
				return {
					changeHeaderState(
						newState,
						newData,
					) {
						switchHeaderState(
							newState,
							newData,
						);
					},

					setCustomBackVisibility(
						visible,
						instant = false,
					) {
						setCustomBackVisibility(
							visible,
							instant,
						);
					},

					closeSearch() {
						handleDropDown(
							false,
						);
					},

					isSearchOpen() {
						return dropOpen;
					},
				};
			},
			[
				dropOpen,
			],
		);

		const handleEsc = (
			e,
		) => {
			let isEscape =
				false;

			if (
				"key" in e
			) {
				isEscape =
					e.key ===
						"Escape" ||
					e.key ===
						"Esc";
			} else {
				isEscape =
					e.keyCode ===
					27;
			}

			if (
				isEscape
			) {
				dataState
					.current
					?.customBack
					?.fn?.();
			}
		};

		useEffect(() => {
			if (
				!loaded.current
			) {
				loaded.current =
					true;

				gsap.to(
					headerRef.current,
					0.5,
					{
						autoAlpha:
							1,

						startAt: {
							yPercent:
								-100,
						},

						yPercent:
							0,

						delay:
							1.5,
					},
				);
			}

			window.addEventListener(
				"keydown",
				handleEsc,
			);

			return () => {
				window.removeEventListener(
					"keydown",
					handleEsc,
				);
			};
		}, []);

		const toggleDropOpen =
			() => {
				handleDropDown(
					!dropOpen,
				);
			};

		const handleFilterItemClick =
			(
				filterValue,
			) => {
				const clickedFilter =
					normalizeCaseFilter(
						filterValue,
					);

				if (
					curFilter ===
					clickedFilter
				) {
					return;
				}

				props.setFilter(
					clickedFilter,
				);

				handleDropDown(
					false,
				);
			};

		const getOptionInteractionProps =
			(filter) => {
				const isActive =
					curFilter ===
					filter.value;

				return {
					isActive,

					onPointerDown:
						isActive
							? undefined
							: (e) => {
									e.stopPropagation();

									if (
										e.pointerType ===
											"touch" ||
										e.pointerType ===
											"pen"
									) {
										e.preventDefault();

										handleFilterItemClick(
											filter.value,
										);
									}
							  },

					onClick:
						isActive
							? undefined
							: (e) => {
									e.stopPropagation();

									handleFilterItemClick(
										filter.value,
									);
							  },

					onKeyDown:
						isActive
							? undefined
							: (e) => {
									if (
										e.key ===
											"Enter" ||
										e.key ===
											" "
									) {
										e.preventDefault();

										e.stopPropagation();

										handleFilterItemClick(
											filter.value,
										);
									}
							  },
				};
			};

		const stopFilterUiPropagation =
			(e) => {
				e.stopPropagation();
			};

		return (
			<div
				className="header_v3"
				style={{
					opacity:
						0,

					pointerEvents:
						"none",
				}}
				ref={
					headerRef
				}
			>
				<div className="header-logo-cnt-v3">
					<Link
						to="/"
						className={
							location.pathname ===
							"/"
								? "disabled-link"
								: "link"
						}
						style={{
							pointerEvents:
								"auto",
						}}
					>
						<div
							className="header-logo"
							ref={
								logoRef
							}
						/>

						<div
							className="lamalogo"
							ref={
								lamaRef
							}
						/>
					</Link>
				</div>

				<div
					className="header-center-btn-cnt"
					ref={
						backBtnRef
					}
				>
					<div
						className="header-back-btn"
						style={{
							pointerEvents:
								"auto",
						}}
						onClick={() => {
							dataState
								.current
								?.customBack
								?.fn?.();
						}}
					>
						<div
							className="back-icon"
							ref={
								backIcon
							}
							style={{
								pointerEvents:
									"none",
							}}
						/>

						<div
							className="back-txt-cnt"
							ref={
								backTxtContainerRef
							}
							style={{
								pointerEvents:
									"none",
							}}
						>
							<div
								className="back-txt"
								ref={
									backTxtPrevRef
								}
							/>

							<div className="nav-header-underline-cnt" />
						</div>

						<div
							className="back-txt-cnt"
							ref={
								backTxtSpacerContainerRef
							}
							style={{
								pointerEvents:
									"none",
							}}
						>
							<div
								className="back-txt spacer"
								ref={
									backTxtSpacerRef
								}
							/>
						</div>

						<div
							className="back-txt-cnt"
							ref={
								backTxtCurrentContainerRef
							}
							style={{
								pointerEvents:
									"none",
							}}
						>
							<div
								className="back-txt"
								ref={
									backTxtRef
								}
							/>
						</div>
					</div>
				</div>

				<div
					className={
						styles.header
					}
					ref={
						searchBarRef
					}
					style={{
						pointerEvents:
							"auto",
					}}
					onPointerDown={
						stopFilterUiPropagation
					}
					onPointerUp={
						stopFilterUiPropagation
					}
					onTouchStart={
						stopFilterUiPropagation
					}
					onTouchEnd={
						stopFilterUiPropagation
					}
					onMouseDown={
						stopFilterUiPropagation
					}
					onClick={
						stopFilterUiPropagation
					}
				>
					<button
						type="button"
						className={
							styles.input
						}
						aria-expanded={
							dropOpen
						}
						aria-label="Choose case filter"
						onPointerDown={
							stopFilterUiPropagation
						}
						onClick={(e) => {
							e.stopPropagation();

							toggleDropOpen();
						}}
					>
						<div
							className={
								styles.searchIcon
							}
						/>

						<div
							className={
								styles.compactValue
							}
						>
							<CaseFilterIdentity
								filter={
									compactFilter.value
								}
								compact
							/>

							<div
								className={
									styles.compactText
								}
								title={
									compactFilter.label
								}
							>
								{
									compactFilter.label
								}
							</div>
						</div>

						<div
							className={`${styles.chevron}${
								dropOpen
									? ` ${styles.chevronOpen}`
									: ""
							}`}
						/>
					</button>

					<div
						className={
							styles.dropdown
						}
						ref={
							searchBarDropDownRef
						}
						onPointerDown={
							stopFilterUiPropagation
						}
						onPointerUp={
							stopFilterUiPropagation
						}
						onTouchStart={
							stopFilterUiPropagation
						}
						onTouchEnd={
							stopFilterUiPropagation
						}
						onMouseDown={
							stopFilterUiPropagation
						}
						onClick={
							stopFilterUiPropagation
						}
						style={{
							opacity:
								0,

							visibility:
								"hidden",
						}}
					>
						<div
							className={
								styles.scopeRow
							}
						>
							{scopeFilters.map(
								(
									filter,
								) => {
									const {
										isActive,
										onPointerDown,
										onClick,
										onKeyDown,
									} =
										getOptionInteractionProps(
											filter,
										);

									return (
										<button
											type="button"
											key={
												filter.value
											}
											className={`${styles.scopeOption}${
												isActive
													? ` ${styles.scopeOptionActive}`
													: ""
											}`}
											disabled={
												isActive
											}
											onPointerDown={
												onPointerDown
											}
											onClick={
												onClick
											}
											onKeyDown={
												onKeyDown
											}
										>
											<CaseFilterIdentity
												filter={
													filter.value
												}
												scope
											/>

											<span>
												{
													filter.label
												}
											</span>

											<span
												className={
													styles.scopeIndicator
												}
											/>
										</button>
									);
								},
							)}
						</div>

						{dynamicCaseFilters.length >
						0 ? (
							<div
								className={
									styles.topicArea
								}
							>
								<div
									className={
										styles.topicOptions
									}
								>
									{dynamicCaseFilters.map(
										(
											filter,
										) => {
											const {
												isActive,
												onPointerDown,
												onClick,
												onKeyDown,
											} =
												getOptionInteractionProps(
													filter,
												);

											return (
												<button
													type="button"
													key={
														filter.value
													}
													className={`${styles.topicOption}${
														isActive
															? ` ${styles.topicOptionActive}`
															: ""
													}`}
													disabled={
														isActive
													}
													onPointerDown={
														onPointerDown
													}
													onClick={
														onClick
													}
													onKeyDown={
														onKeyDown
													}
													title={
														filter.label
													}
												>
													<CaseFilterIdentity
														filter={
															filter.value
														}
													/>

													<span
														className={
															styles.topicLabel
														}
													>
														{
															filter.label
														}
													</span>
												</button>
											);
										},
									)}
								</div>
							</div>
						) : null}
					</div>
				</div>

				<NavMenu
					reference={
						contactCnt
					}
					setFilter={
						props.setFilter
					}
				/>
			</div>
		);
	},
);

export default Header;